/**
 * Helpers for Command Center deal poster drafts on the Deal Posters page.
 */

export function isDealPosterDraftAdminOrBuyer(role) {
  return role === "admin" || role === "buyer";
}

export function safeDraftField(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number" && !Number.isFinite(value)) {
    return null;
  }
  const text = String(value).trim();
  if (!text || text === "undefined" || text === "null" || text === "Infinity" || text === "NaN") {
    return null;
  }
  return text;
}

export function formatDraftTimestamp(value) {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? new Date(parsed).toLocaleString() : null;
  }
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return null;
    }
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toLocaleString();
  }
  return null;
}

export function draftCreatedAtMs(draft) {
  const raw = draft?.created_at;
  if (!raw) {
    return 0;
  }
  if (typeof raw === "string") {
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof raw.toDate === "function") {
    try {
      return raw.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (raw instanceof Date) {
    return raw.getTime();
  }
  return 0;
}

export function sortDraftsByNewest(drafts) {
  return [...drafts].sort((a, b) => draftCreatedAtMs(b) - draftCreatedAtMs(a));
}

function draftDedupeKey(draft) {
  const store = safeDraftField(draft.store_name) || "";
  const product = safeDraftField(draft.product_name) || "";
  const expiration = safeDraftField(draft.expiration_date) || "";
  return `${store}|${product}|${expiration}`;
}

/**
 * When multiple active drafts share store + product + expiration, keep only the newest.
 */
export function dedupeDraftsKeepNewest(drafts) {
  const sorted = sortDraftsByNewest(drafts);
  const seen = new Set();
  const visible = [];
  let hiddenDuplicateCount = 0;

  for (const draft of sorted) {
    const key = draftDedupeKey(draft);
    if (seen.has(key)) {
      hiddenDuplicateCount += 1;
      continue;
    }
    seen.add(key);
    visible.push(draft);
  }

  return { drafts: visible, hiddenDuplicateCount };
}

/**
 * Human-readable review note for Command Center drafts (shown in Notes column).
 */
export function buildCommandCenterReviewNote(draft) {
  const product = safeDraftField(draft.product_name) || "Unknown product";
  const store = safeDraftField(draft.store_name) || "Unknown store";
  const brand = safeDraftField(draft.brand);
  const qty = draft.quantity;
  const expiration = safeDraftField(draft.expiration_date);
  const days = draft.days_until_expiration;
  const action = safeDraftField(draft.suggested_action);
  const runId = safeDraftField(draft.source_run_id);
  const cardId = safeDraftField(draft.source_card_id);

  const parts = [
    "Command Center flagged this as a deal poster opportunity based on expiring inventory.",
    `Product: ${product}.`,
    `Store: ${store}.`,
  ];

  if (brand) {
    parts.push(`Brand: ${brand}.`);
  }
  if (Number.isFinite(Number(qty))) {
    parts.push(`Quantity: ${Number(qty)}.`);
  }
  if (expiration) {
    parts.push(`Expiration: ${expiration}.`);
  } else if (Number.isFinite(Number(days)) && Number(days) >= 0) {
    parts.push(`Expiration: in ${Number(days)} days.`);
  }
  if (action) {
    parts.push(`Suggested action: ${action}`);
  }
  if (runId) {
    parts.push(`Source run: ${runId}.`);
  }
  if (cardId) {
    parts.push(`Source card: ${cardId}.`);
  }

  return parts.join(" ");
}

function buildDealTextFromDraft(draft) {
  const product = safeDraftField(draft.product_name) || "Unknown product";
  const brand = safeDraftField(draft.brand);
  const qty = draft.quantity;
  const exp = safeDraftField(draft.expiration_date);
  const days = draft.days_until_expiration;

  const parts = [];
  if (brand && !product.toLowerCase().includes(brand.toLowerCase())) {
    parts.push(brand);
  }
  parts.push(product);

  if (Number.isFinite(Number(qty))) {
    parts.push(`Qty ${Number(qty)}`);
  }
  if (exp) {
    parts.push(`Exp ${exp}`);
  } else if (Number.isFinite(Number(days)) && Number(days) >= 0) {
    parts.push(`Exp in ${Number(days)} days`);
  }

  return parts.join(" — ");
}

/**
 * Append a review row to existing CSV data, unioning column keys so Notes and
 * other Command Center fields remain visible in the editor.
 */
export function appendReviewRowToCsvData(existingRows, reviewRow) {
  if (!Array.isArray(existingRows) || existingRows.length === 0) {
    return [reviewRow];
  }

  const allKeys = new Set([
    ...Object.keys(existingRows[0] || {}),
    ...Object.keys(reviewRow || {}),
  ]);

  const normalizeRow = (row) => {
    const normalized = {};
    for (const key of allKeys) {
      const value = row?.[key];
      if (value === null || value === undefined) {
        normalized[key] = "";
      } else if (typeof value === "number" && !Number.isFinite(value)) {
        normalized[key] = "";
      } else {
        normalized[key] = value;
      }
    }
    return normalized;
  };

  return [...existingRows.map(normalizeRow), normalizeRow(reviewRow)];
}

/**
 * Build a single deal-table row for manual review in CSV Editor (no auto-publish).
 */
export function buildReviewRowFromDraft(draft) {
  const store = safeDraftField(draft.store_name) || "";
  const product = safeDraftField(draft.product_name) || "Unknown product";
  const brand = safeDraftField(draft.brand);
  const qty = draft.quantity;
  const exp = safeDraftField(draft.expiration_date);

  const row = {
    Store: store,
    Category: "Markdown",
    "Deal Text": buildDealTextFromDraft(draft),
    Date: exp || new Date().toISOString().slice(0, 10),
    "POS Link": "",
    Notes: buildCommandCenterReviewNote(draft),
    _command_center_draft_id: draft.id || null,
    _command_center_source_run_id: safeDraftField(draft.source_run_id),
    _command_center_source_card_id: safeDraftField(draft.source_card_id),
    _command_center_reason: "Expiring inventory opportunity",
    _command_center_source: "Command Center",
  };

  if (brand) {
    row.Brand = brand;
  }
  if (Number.isFinite(Number(qty))) {
    row.Quantity = String(Number(qty));
  }
  row.Product = product;

  return row;
}
